(() => {
  if (window.uklEliteProductReady) return;
  window.uklEliteProductReady = true;

  const resolveActiveVariant = (section) => {
    if (!section) return null;

    const variantId = section.querySelector('.js-product-form-main input[name="id"]')?.value;
    const productJson = section.querySelector('variant-picker script[type="application/json"]');
    if (!variantId || !productJson) return null;

    try {
      const data = JSON.parse(productJson.textContent);
      return data?.product?.variants?.find((variant) => String(variant.id) === String(variantId)) || null;
    } catch (_error) {
      return null;
    }
  };

  const metresFromValue = (value) => {
    const match = String(value || '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(?:m|metres?|meters?)$/i);
    return match ? Number.parseFloat(match[1].replace(',', '.')) : null;
  };

  const replaceToken = (template, token, value) => String(template || '').split(token).join(value);

  const setupLengthDisclosures = (section) => {
    if (!section) return;

    section.querySelectorAll('.ukl-length-selector[data-selector-type="listed"]').forEach((selector) => {
      if (selector.dataset.uklLengthDisclosureReady === 'true') return;

      const choices = selector.querySelector('[data-ukl-length-choices]');
      if (!choices) return;

      const overflowInputs = [...choices.querySelectorAll('input[type="radio"]')].filter((input) => {
        const metres = metresFromValue(input.value);
        return metres !== null && metres > 20;
      });
      if (!overflowInputs.length) return;

      overflowInputs.forEach((input) => {
        input.dataset.uklLengthOverflow = 'true';
      });

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ukl-length-more';
      button.setAttribute('aria-controls', choices.id);

      const hiddenOverflowCount = () => overflowInputs.filter((input) => !input.checked).length;
      const setExpanded = (expanded) => {
        selector.classList.toggle('is-expanded', expanded);
        overflowInputs.forEach((input) => {
          input.hidden = !expanded && !input.checked;
        });
        button.setAttribute('aria-expanded', String(expanded));
        const count = hiddenOverflowCount();
        button.textContent = expanded
          ? selector.dataset.uklLengthLessLabel
          : replaceToken(selector.dataset.uklLengthMoreTemplate, '[count]', count);
        button.hidden = !expanded && count === 0;
      };

      button.addEventListener('click', () => {
        setExpanded(!selector.classList.contains('is-expanded'));
      });

      selector.addEventListener('change', (event) => {
        const selectedOverflow = event.target?.dataset.uklLengthOverflow === 'true';
        setExpanded(selector.classList.contains('is-expanded') || selectedOverflow);
      });

      choices.append(button);
      selector.dataset.uklLengthDisclosureReady = 'true';
      setExpanded(overflowInputs.some((input) => input.checked));
    });
  };

  const enhanceCutLengthField = (section) => {
    if (!section?.querySelector('.product--ukl-linear')) return false;

    const configuredInput = section.querySelector(
      '.gpo-input[data-name="properties[Required Cut Length]"], .gpo-input[data-field-name="Required Cut Length"]'
    );
    const fallbackElement = [...section.querySelectorAll('.gpo-element')].find((element) => {
      const label = element.querySelector('.gpo-label')?.textContent || '';
      return label.trim().toLowerCase().includes('required cut length');
    });
    const cutElement = configuredInput?.closest('.gpo-element') || fallbackElement;
    const input = configuredInput || cutElement?.querySelector('input');
    const helper = cutElement?.querySelector('.gpo-helptext');
    const messages = cutElement?.querySelector('.messages');
    const product = section.querySelector('.js-product');
    const lengthSelector = section.querySelector('.ukl-length-selector');
    const form = section.querySelector('.js-product-form-main');
    if (!cutElement || !input || !helper || !messages || !product || !lengthSelector || !form) return false;
    if (cutElement.dataset.uklEnhanced === 'true') return true;

    const customSelect = lengthSelector.querySelector('custom-select');
    const listedChoices = [...lengthSelector.querySelectorAll('input[type="radio"]')];
    const dropdownChoices = [...lengthSelector.querySelectorAll('.custom-select__option[data-value]')];
    const rawChoices = listedChoices.length ? listedChoices : dropdownChoices;
    const choices = rawChoices
      .map((choice) => {
        const value = choice.value || choice.dataset.value;
        return { choice, value, metres: metresFromValue(value) };
      })
      .filter((item) => item.metres !== null)
      .sort((left, right) => left.metres - right.metres);
    if (!choices.length) return false;

    cutElement.dataset.uklEnhanced = 'true';
    input.inputMode = 'decimal';
    const placeholderTemplate = product.dataset.uklCutPlaceholder || '';
    input.placeholder = placeholderTemplate;
    helper.textContent = product.dataset.uklCutHelper;
    if (!helper.id) helper.id = `ukl-cut-helper-${section.id}`;
    if (!messages.id) messages.id = `ukl-cut-status-${section.id}`;
    messages.setAttribute('role', 'status');
    messages.setAttribute('aria-live', 'polite');
    input.setAttribute('aria-describedby', `${helper.id} ${messages.id}`);

    const state = { active: false, applying: false };
    const max = choices[choices.length - 1].metres;

    const showMessage = (message, isError = false) => {
      messages.textContent = message || '';
      messages.classList.toggle('ukl-gpo-cut-status--error', isError);
      input.setCustomValidity(isError ? message : '');
      if (isError) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    };

    const selectedValue = () => {
      const selectedRadio = lengthSelector.querySelector('input[type="radio"]:checked');
      if (selectedRadio) return selectedRadio.value;

      return lengthSelector.querySelector('.custom-select__option[aria-selected="true"]')?.dataset.value || '';
    };

    const selectChoice = (item) => {
      if (!item || selectedValue() === item.value) return;
      state.applying = true;

      if (item.choice.matches('input[type="radio"]')) {
        item.choice.checked = true;
        item.choice.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (customSelect && typeof customSelect.selectOption === 'function') {
        customSelect.selectOption(item.choice);
      }

      state.applying = false;
    };

    const applyCutLength = () => {
      const raw = input.value.trim().replace(',', '.');
      if (!raw) {
        showMessage('');
        state.active = false;
        return true;
      }

      if (!/^\d+(?:\.\d+)?$/.test(raw) || Number(raw) <= 0) {
        showMessage(product.dataset.uklCutInvalid, true);
        return false;
      }

      const requested = Number(raw);
      if (requested > max) {
        const maxLabel = Number.isInteger(max) ? String(max) : String(max);
        showMessage(replaceToken(product.dataset.uklCutLimit, '[max]', maxLabel), true);
        return false;
      }

      // The requested finished length is rounded to the nearest physical cut
      // point in production. Charge the next whole-metre product variant.
      const billedMetres = Math.ceil(requested - Number.EPSILON);
      const target = choices.find((item) => item.metres === billedMetres);
      if (
        !target ||
        target.choice.disabled ||
        target.choice.classList.contains('is-disabled') ||
        target.choice.classList.contains('is-unavailable')
      ) {
        showMessage(product.dataset.uklCutInvalid, true);
        return false;
      }

      selectChoice(target);
      const activeVariant = resolveActiveVariant(section);
      if (
        !activeVariant ||
        activeVariant.available === false ||
        !activeVariant.options?.includes(target.value)
      ) {
        state.active = false;
        showMessage(product.dataset.uklCutInvalid, true);
        return false;
      }
      state.active = true;

      let message = replaceToken(product.dataset.uklCutApplied, '[priced]', String(billedMetres));
      message = replaceToken(message, '[requested]', String(requested));
      showMessage(message);
      return true;
    };

    input.addEventListener('input', applyCutLength);
    input.addEventListener('change', applyCutLength);
    form.addEventListener('submit', (event) => {
      if (applyCutLength()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      input.reportValidity();
      input.focus();
    }, true);

    lengthSelector.addEventListener('change', () => {
      if (state.active && !state.applying) window.requestAnimationFrame(applyCutLength);
    });

    applyCutLength();
    return true;
  };

  const cutFieldObservers = new WeakMap();

  const disconnectCutFieldObserver = (section) => {
    const observer = cutFieldObservers.get(section);
    if (!observer) return;

    observer.disconnect();
    window.cancelAnimationFrame(observer.uklFrame);
    window.clearTimeout(observer.uklTimeout);
    cutFieldObservers.delete(section);
  };

  const observeCutLengthField = (section) => {
    if (!section || cutFieldObservers.has(section)) return;
    if (enhanceCutLengthField(section)) return;

    const observer = new MutationObserver(() => {
      if (observer.uklFrame) return;

      observer.uklFrame = window.requestAnimationFrame(() => {
        observer.uklFrame = 0;
        if (!section.isConnected || enhanceCutLengthField(section)) {
          disconnectCutFieldObserver(section);
        }
      });
    });

    cutFieldObservers.set(section, observer);
    observer.observe(section.querySelector('.product-info') || section, { childList: true, subtree: true });
    observer.uklTimeout = window.setTimeout(() => disconnectCutFieldObserver(section), 8000);
  };

  const cableCardObservers = new WeakMap();

  const markStandaloneCableCard = (section) => {
    if (!section?.querySelector('.product--ukl-linear')) return false;

    return [...section.querySelectorAll('.gpo-container')].some((container) => {
      if (container.closest('[data-ukl-switchboard-cable-slot]')) return false;
      const fields = [...container.querySelectorAll('.gpo-element')];
      if (fields.length !== 1 || !fields[0].querySelector('.gpo-dropdown')) return false;

      const input = fields[0].querySelector('[data-field-name], [data-name]');
      const label = fields[0].querySelector('.gpo-label');
      const name = String((input && (input.dataset.fieldName || input.dataset.name)) || label?.textContent || '')
        .trim().toLowerCase().replace(/^properties\[(.+)\]$/, '$1').replace(/\s+/g, ' ');
      if (name !== 'cable length' && name !== 'extension cable length') return false;

      const appBlock = container.closest('.shopify-app-block') || container;
      if (appBlock.querySelectorAll('.gpo-container').length !== 1) return false;
      const sourceBlock = appBlock.closest('.product-info__block');
      if (!sourceBlock) return false;

      sourceBlock.classList.add('ukl-standalone-cable-card');
      fields[0].classList.add('ukl-standalone-cable-field');
      return true;
    });
  };

  const disconnectCableCardObserver = (section) => {
    const observer = cableCardObservers.get(section);
    if (!observer) return;
    observer.disconnect();
    window.clearTimeout(observer.uklTimeout);
    cableCardObservers.delete(section);
  };

  const observeStandaloneCableCard = (section) => {
    if (!section || cableCardObservers.has(section) || markStandaloneCableCard(section)) return;
    if (!section.querySelector('.product--ukl-linear')) return;

    const observer = new MutationObserver(() => {
      if (!section.isConnected || markStandaloneCableCard(section)) disconnectCableCardObserver(section);
    });
    cableCardObservers.set(section, observer);
    observer.observe(section.querySelector('.product-info') || section, { childList: true, subtree: true });
    observer.uklTimeout = window.setTimeout(() => disconnectCableCardObserver(section), 15000);
  };

  const syncProductState = (section, variant) => {
    if (!section) return;

    const activeVariant = variant || resolveActiveVariant(section);
    const sourcePrice = section.querySelector('.product-info__price > .price');
    const stickyPrice = section.querySelector('sticky-atc-panel .price');
    const stickyVariant = section.querySelector('[data-ukl-sticky-variant]');
    const stickySubmit = section.querySelector('[data-ukl-sticky-submit]');
    const addButton = section.querySelector('.js-product-form-main [name="add"]');
    const availability = section.querySelector('[data-ukl-product-availability]');

    if (sourcePrice && stickyPrice) {
      stickyPrice.className = sourcePrice.className;
      stickyPrice.innerHTML = sourcePrice.innerHTML;
    }

    if (addButton && !addButton.disabled && addButton.dataset.addToCartText) {
      addButton.textContent = addButton.dataset.addToCartText;
    }

    if (stickyVariant) {
      const variantTitle = activeVariant?.public_title || activeVariant?.title || '';
      stickyVariant.textContent = variantTitle;
      stickyVariant.hidden = !variantTitle || variantTitle === 'Default Title';
    }

    if (stickySubmit) {
      stickySubmit.disabled = Boolean(
        (activeVariant && !activeVariant.available) ||
        (addButton && addButton.disabled)
      );
    }

    if (availability) {
      const availabilityText = availability.querySelector('[data-ukl-product-availability-text]');
      const hasVariant = Boolean(activeVariant);
      const isAvailable = hasVariant ? Boolean(activeVariant.available) : Boolean(addButton && !addButton.disabled);
      const text = hasVariant
        ? (isAvailable ? availability.dataset.inStockText : availability.dataset.soldOutText)
        : (isAvailable ? availability.dataset.inStockText : availability.dataset.unavailableText);

      availability.dataset.available = String(isAvailable);
      if (availabilityText) availabilityText.textContent = text;
    }
  };

  const syncStickyPosition = (section) => {
    if (!section) return;

    const panel = section.querySelector('sticky-atc-panel');
    const addButton = section.querySelector('.js-product-form-main [name="add"]');
    if (!panel || !addButton) return;

    // The theme's native panel appears whenever the main CTA is outside the
    // viewport, including while it is still further down the page. Keep the
    // panel out of the gallery and reveal it only after the customer has
    // scrolled past the primary purchase action.
    const buyButtonsBlock = addButton.closest('.product-info__block');
    const isMobile = window.matchMedia('(max-width: 768.98px)').matches;
    const isPastBuyBox = (buyButtonsBlock || addButton).getBoundingClientRect().bottom <= 0;
    const isNearPageEnd = window.scrollY + window.innerHeight + 100 > document.documentElement.scrollHeight;
    const shouldShow = isMobile && isPastBuyBox && !isNearPageEnd;

    panel.classList.toggle('ukl-sticky-atc--visible', shouldShow);
    panel.classList.toggle('ukl-sticky-atc--before-buybox', !shouldShow);
    if (shouldShow) {
      panel.removeAttribute('aria-hidden');
    } else {
      panel.setAttribute('aria-hidden', 'true');
    }
  };

  let stickyPositionFrame;
  const scheduleStickyPositionSync = () => {
    if (stickyPositionFrame) return;

    stickyPositionFrame = window.requestAnimationFrame(() => {
      stickyPositionFrame = null;
      document.querySelectorAll('.cc-main-product').forEach(syncStickyPosition);
    });
  };

  const syncAllProducts = () => {
    document.querySelectorAll('.cc-main-product').forEach((section) => {
      setupLengthDisclosures(section);
      syncProductState(section);
      observeCutLengthField(section);
      observeStandaloneCableCard(section);
      syncStickyPosition(section);
    });
  };

  const initialiseProductState = () => {
    syncAllProducts();
  };

  document.addEventListener('on:variant:change', (event) => {
    const section = event.target.closest('.cc-main-product');
    window.requestAnimationFrame(() => {
      setupLengthDisclosures(section);
      syncProductState(section, event.detail?.variant);
      observeCutLengthField(section);
      observeStandaloneCableCard(section);
      syncStickyPosition(section);
    });
  });

  document.addEventListener('click', (event) => {
    const stickySubmit = event.target.closest('[data-ukl-sticky-submit]');
    if (!stickySubmit || stickySubmit.disabled) return;

    const section = stickySubmit.closest('.cc-main-product');
    const addButton = section?.querySelector('.js-product-form-main [name="add"]');
    if (addButton && !addButton.disabled) addButton.click();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches('.cc-main-product')
      ? event.target
      : event.target.querySelector('.cc-main-product');
    setupLengthDisclosures(section);
    syncProductState(section);
    observeCutLengthField(section);
    observeStandaloneCableCard(section);
    syncStickyPosition(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches('.cc-main-product')
      ? event.target
      : event.target.querySelector('.cc-main-product');
    disconnectCutFieldObserver(section);
    disconnectCableCardObserver(section);
  });

  window.addEventListener('scroll', scheduleStickyPositionSync, { passive: true });
  window.addEventListener('resize', scheduleStickyPositionSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseProductState, { once: true });
  } else {
    initialiseProductState();
  }
})();
